import React, { useEffect, useMemo, useState } from 'react';
import styles from './VideoLogo.module.scss';
import { useLogoContext } from '../../context/LogoContext';
import useObjectSimulation from '../../hooks/useObjectSimulation';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

const LOGO_TRANSITION_TIME = 500;

const VideoLogo = ({
  parentNode,
  id,
  logo = null,
  mixedOrientation,
  videoMaxDimensions,
  played,
  intro,
  outro,
  duration,
  multiplyMode = false,
  isEmptyVideo = false,
  isMultiplyMode = false,
}) => {
  const [logoPosition, setLogoPosition] = useState({});
  const [logoDimensions, setLogoDimensions] = useState({ width: 0, height: 0 });
  const [imageDimensions, setImageDimensions] = useState(null);
  const [opacity, setOpacity] = useState(0);
  const [recalculating, setRecalculating] = useState(false);

  const { t } = useTranslation();

  const { isLogoEditor, currentLogoFromEditor } = useLogoContext();
  const currentLogo = useMemo(() => isLogoEditor ? currentLogoFromEditor : logo, [logo, currentLogoFromEditor, isLogoEditor]);


  const { 
    objectPosition, 
    objectSize, 
    loading
  } = useObjectSimulation({
    parentNode,
    id,
    mode: 'logo',
    margin_x: currentLogo?.marginX,
    margin_y: currentLogo?.marginY,
    position: currentLogo?.position,
    size: currentLogo?.size,
    imageDimensions,
    mixedOrientation,
    maxDimensions: videoMaxDimensions,
    multiplyMode,
    isEmptyVideo,
  });


  useEffect(() => {
    if (currentLogo) {
      const img = new Image();
      setRecalculating(true);
      img.onload = () => {
        const imageDimensions = { width: 0, height: 0 };
        imageDimensions.width = img.width;
        imageDimensions.height = img.height;
        setImageDimensions(imageDimensions);
      };
      img.src = currentLogo?.url ?? currentLogo?.path;
    }
  }, [currentLogo]);

  useEffect(() => {
    if (logo?.url ?? logo?.path) {
      let newOpacity = 0;
      const introDuration = intro ? intro.duration : 0;
      const outroDuration = outro ? outro.duration : 0;
      if (played > introDuration) {
        if (played - introDuration <= LOGO_TRANSITION_TIME) {
          newOpacity = Math.min(1, ((played - introDuration) * 0.2) / 100);
        } else {
          newOpacity = 1;
        }
      }
      if (played > duration - outroDuration - LOGO_TRANSITION_TIME) {
        newOpacity = Math.max(0, ((duration - outroDuration - played) * 0.2) / 100);
      }
      setOpacity(newOpacity);
    }
  }, [played, intro, outro]);

  useEffect(() => {
    setRecalculating(false);
    setLogoPosition(objectPosition);
  }, [objectPosition]);

  useEffect(() => {
    if (objectSize?.width && objectSize?.height) {
      setLogoDimensions(objectSize);
    }
  }, [objectSize]);

  return !recalculating && (currentLogo?.url ?? currentLogo?.path) && (isMultiplyMode || !loading) ? (
    <div
      className={cx(styles.logoContainer, { [styles.overPlayer]: isLogoEditor, [styles.logoMultipleContainer]: multiplyMode })}
      style={{ ...logoPosition, opacity: Number(isLogoEditor) || opacity }}
    >
      <div className={styles.wrapper}>
        <img src={currentLogo?.url ?? currentLogo?.path} style={logoDimensions} alt={t('logo_chooser.header')} />
      </div>
    </div>
  ) : null;
};

export default VideoLogo;
